// src/components/StatsHelpModal.jsx
import React from 'react';

const STATS_HELP = {
  SERVICE: {
    title: 'Statistiques de Service',
    color: '#1976D2',
    items: [
      { name: 'Aces & services gagnants', desc: "Points gagnés directement sur le service à l'aide d'un ace ou d'un service gagnant." },
      { name: 'Doubles fautes', desc: "Deux fautes de service consécutives = point perdu directement. À minimiser." },
      { name: '% balles de break sauvées', desc: "Sur les points où l'adversaire avait une balle de break (0-40, 15-40, 30-40, 40-AD), pourcentage sauvés. Indicateur de solidité mentale au service." },
      { name: '% 1er service', desc: "Pourcentage de 1ers services réussis que le point soit ensuite gagné ou perdu. Un bon serveur tourne autour de 60-70%." },
      { name: '% pts gagnés 1er service', desc: "Sur les points où le 1er service est réussi, combien ont été remportés. Mesure l'efficacité quand le 1er service est réussi." },
      { name: '% pts gagnés 2nd service', desc: "Sur les points joués avec le 2nd service, combien ont été remportés. En dessous de 50% c'est une faiblesse." },
      { name: 'Zones 1er / 2nd service', desc: "Répartition des services par zone (T, corps, extérieur) et efficacité par zone. Permet d'identifier les zones à privilégier." },
      { name: 'Effets service', desc: "Répartition des effets utilisés (Plat, Slicé, Lifté) et leur efficacité. Un service plat est plus rapide, le slicé sort de la zone de frappe adverse." },
    ]
  },
  RETOUR: {
    title: 'Statistiques de Retour',
    color: '#FF9800',
    items: [
      { name: '% balles de break converties', desc: "Sur les balles de break obtenues, combien ont effectivement été converties en break. Mesure l'efficacité dans les moments clés du retour." },
      { name: '% pts gagnés en retour 1er service', desc: "Pourcentage de points remportés sur les jeux où le joueur était en position de retourner un 1er service. Au-dessus de 40% = très bon retourneur." },
      { name: '% pts gagnés en retour 2nd service', desc: "Pourcentage de points remportés sur les jeux où le joueur était en position de retourner un 2nd service. Au-dessus de 60% = très bon retourneur." },
    ]
  },
  POINTS: {
    title: 'Statistiques de Points',
    color: '#388E3C',
    items: [
      { name: 'Total points gagnés', desc: "Pourcentage de tous les points remportés sur le match. Inclut les gains directs ET les fautes adverses. Indicateur global du rapport de force." },
      { name: 'Total nb de points gagnants', desc: "Coups qui terminent directement le point sans que l'adversaire ne touche la balle. Différent de 'Total Points gagnés' qui inclut aussi les fautes adverses." },
      { name: '% points gagnés au service', desc: "Pourcentage de points remportés sur les jeux de service." },
      { name: '% points gagnés au retour', desc: "Pourcentage de points remportés sur les jeux de retour." },
      { name: '% pts gagnés au filet', desc: "Pourcentage de points remportés quand le joueur monte au filet (volées, smashes). Un bon volleyeur dépasse 70%." },
      { name: 'Fautes directes', desc: "Points perdus sur une erreur non forcée. Un ratio fautes/gagnants élevé indique un jeu trop risqué." },
      { name: 'Points sous pression', desc: "Ensemble de statistiques mesurant le comportement dans les moments décisifs du match." },
      { name: '% balles de break sauvées', desc: "Sur les points où l'adversaire avait une balle de break (0-40, 15-40, 30-40, 40-AD), pourcentage sauvés. Indicateur de solidité mentale au service." },
      { name: '% balles de set sauvées', desc: "Sur les points où l'adversaire avait une balle de set (balle de break dans un jeu décisif pour le set), pourcentage sauvés." },
      { name: '% balles de match sauvées', desc: "Sur les points où l'adversaire avait une balle de match, pourcentage sauvés. Mesure la capacité à survivre dans les moments ultimes." },
      { name: '% pts gagnés à 40-40', desc: "Pourcentage de points remportés à égalité. Indique la solidité dans les moments de pression maximale intra-jeu." },
      { name: 'Situation critique serveur', desc: "Scores 0-30, 0-40, 15-40, 30-40, 40-AD : le serveur est en danger. Mesure la résilience quand on est dos au mur." },
      { name: 'Situation critique retourneur', desc: "Scores 30-0, 40-0, 40-15, 40-30, AD-40 : le serveur domine. Mesure la capacité à conclure quand on a l'avantage." },
      { name: '% jeux service favorables', desc: "Parmi les jeux de service où le joueur a mené 30-0, 40-0 ou 40-15, pourcentage finalement remportés. Mesure la capacité à conclure quand on domine." },
      { name: '% jeux retour favorables', desc: "Parmi les jeux de retour où le joueur a mené 0-30, 0-40 ou 15-40, pourcentage finalement convertis. Mesure l'efficacité dans les jeux dominés au retour." },
      { name: '% pts après 2 pts perdus', desc: "Après avoir perdu 2 points consécutifs, capacité à rebondir sur le suivant. Mesure la résilience mentale." },
      { name: '% pts après 2 pts gagnés', desc: "Après avoir gagné 2 points consécutifs, capacité à maintenir l'élan. Mesure la régularité dans le momentum." },
      { name: 'Série de points perdus', desc: "Nombre maximum de points consécutifs perdus. Indique les périodes de décrochage dans le match." },
    ]
  },
  JEUX: {
    title: 'Statistiques de Jeux',
    color: '#7B1FA2',
    items: [
      { name: 'Total de jeux gagnés', desc: "Nombre total de jeux remportés à la fois au service et au retour." },
      { name: 'Jeux gagnés au service', desc: "Nombre de jeux remportés quand le joueur servait. Un bon serveur gagne la grande majorité de ses jeux de service." },
      { name: 'Jeux gagnés au retour (breaks)', desc: "Nombre de jeux remportés quand l'adversaire servait = breaks réalisés. Chaque break est précieux pour remporter un set." },
    ]
  },
};

function StatsHelpModal({ section, onClose }) {
  const help = STATS_HELP[section];
  if (!help) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '16px',
          padding: '20px', maxWidth: '380px', width: '100%',
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: help.color }}>
            {help.title}
          </h3>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', fontSize: '20px',
            cursor: 'pointer', color: '#999', lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Liste des stats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {help.items.map((item, i) => (
            <div key={i} style={{
              padding: '10px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: `3px solid ${help.color}`,
            }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: help.color, marginBottom: '4px' }}>
                {item.name}
              </div>
              <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>
                {item.desc}
              </div>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{
          marginTop: '16px', width: '100%', padding: '11px',
          backgroundColor: help.color, color: 'white', border: 'none',
          borderRadius: '8px', fontSize: '14px', fontWeight: '600',
          cursor: 'pointer',
        }}>
          Fermer
        </button>
      </div>
    </div>
  );
}

export default StatsHelpModal;